import classNames from 'classnames';
import React, { useRef, useState } from 'react';
import { Button } from 'src/common/button';
import { Journey } from 'src/common/interfaces/journey.interface';
import { TooltipComponent } from 'src/common/tooltip/component';
import { NeueLoadingSpinner } from 'src/editor/content-creation/components/loading/spinner';
import { MiniModal } from 'src/editor/content-creation/components/mini-modal';
import { CheckmarkRegularIcon, DownloadStrongIcon } from 'src/monet/icons';
import { functionNoop } from 'src/utils/function/noop';
import { apiGetJourneyInsights } from 'src/utils/journeyApi';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';
import { buildInsightsViewDetails } from '../../helpers';
import { formatDurationInHMS } from 'src/utils/date';
import { VisitorView } from 'src/editor/insights/constants';
import { DashboardInsightsJourneyDownloadVisitorsCSVLink } from './download-visitors-csv-link';
import delay from 'lodash/delay';

export type JourneyDownloadVisitor = Record<Journey['uuid'], { enable: boolean; name: string }>;

interface Props {
  enabled: boolean;
  journeys: JourneyDownloadVisitor;
}

const DownloadStatus = {
  IDLE: 0,
  IN_PROGRESS: 1,
  COMPLETED: 2,
} as const;

type VisitorsJourneyNameMapping<T> = { name: string; data: T };

export const DashboardInsightsJourneyDownloadVisitorsCSV = ({ journeys, enabled }: Props) => {
  const [downloadStatus, setDownloadStatus] = useState<number>(DownloadStatus.IDLE);
  const downloadedFileCountRef = useRef(0);

  const processJourneyViews = (views: VisitorView[]) =>
    views.map((view) => {
      let { alias_id, completed_percentage, created_at, duration, email, ip_address, location } = view;
      let builtDetails = buildInsightsViewDetails(view);
      let viewerName = builtDetails?.name;
      let companyName = builtDetails?.company?.name;
      let deviceType = builtDetails?.device_type?.os;

      const phoneNumber =
        builtDetails?.visitor?.custom_field_values?.find((field: any) => field.field === 'phone_number')?.value || '';

      return {
        name: viewerName,
        viewed: created_at,
        time_spent: duration ? formatDurationInHMS(duration) : '-',
        completion: `${Math.round(completed_percentage)}%`,
        email_address: email,
        company: companyName,
        location,
        phone_number: phoneNumber,
        ip_address,
        alias: alias_id,
        device: deviceType,
      };
    });

  type Visitors = VisitorsJourneyNameMapping<ReturnType<typeof processJourneyViews>>;

  const [visitorsData, setVisitorsData] = useState<Visitors[]>([]);
  const modalOpenRef = useRef(functionNoop);
  const modalCloseRef = useRef(functionNoop);

  const insightsFetcherRef = useRefCallback((uuid: string) =>
    apiGetJourneyInsights(uuid, undefined, {
      all: true,
      time_period: { end_time: new Date().toISOString() },
    })
      .then((response) => response.views)
      .then(processJourneyViews)
  );

  const makeDownloadIdle = () => {
    setDownloadStatus(DownloadStatus.IDLE);
  };

  const onMiniModalClose = (completed?: boolean) => {
    if (completed) {
      delay(makeDownloadIdle, 1_100);
    } else {
      makeDownloadIdle();
    }
    modalCloseRef.current();
  };

  const onDownloadCSVClick = () => {
    modalOpenRef.current();
  };

  const onMiniModalDownloadClick = async () => {
    setDownloadStatus(DownloadStatus.IN_PROGRESS);
    const journeyVisitorMapping: Record<Journey['name'], any[]> = {};
    let names: Journey['name'][] = [];
    for (const journeyUUID of Object.keys(journeys)) {
      const journeyVisitors = await insightsFetcherRef(journeyUUID);
      const { name } = journeys[journeyUUID];

      journeyVisitorMapping[name] = journeyVisitors;
      names.push(name);
    }
    const allVisitors = await Promise.all(Object.keys(journeys).map((uuid) => insightsFetcherRef(uuid)));
    const nameVisitorsMapping: Visitors[] = names.map((name, index) => ({
      name,
      data: allVisitors[index],
    }));
    setVisitorsData(nameVisitorsMapping);
    setDownloadStatus(DownloadStatus.IDLE);
  };

  const { length: activeJourneyLength } = Object.keys(journeys);

  const renderDownloadIcon = () => {
    return (
      <div className='flex gap-2 shrink-0'>
        <TooltipComponent
          placement='top'
          tooltipContent='Download visitors for the selected Journeys'
          theme='journey'
          styling='tooltip'
        >
          <div className='flex gap-2'>
            <DownloadStrongIcon
              className={classNames('text-bedrock-black', {
                'cursor-pointer': downloadStatus === DownloadStatus.IDLE,
              })}
            />
          </div>
        </TooltipComponent>

        <div
          className={classNames(
            'transition-opacity text-[10px] leading-[18px] font-[450]',
            'flex justify-center items-center min-w-[1.125rem]',
            'ml-auto px-1.5 rounded-lg text-bedrock-white bg-bedrock-brand-secondary',
            {
              'opacity-100': activeJourneyLength > 0,
              'opacity-0': activeJourneyLength === 0,
            }
          )}
        >
          {activeJourneyLength}
        </div>
        {downloadStatus === DownloadStatus.COMPLETED && (
          <CheckmarkRegularIcon className='text-bedrock-brand-secondary' />
        )}
      </div>
    );
  };

  const isDownloadInProgress = downloadStatus === DownloadStatus.IN_PROGRESS;

  return (
    <>
      <button
        disabled={!enabled}
        onClick={onDownloadCSVClick}
        className={classNames('px-4 transition-opacity disabled:opacity-50', {
          'pointer-events-none disabled': !enabled || isDownloadInProgress,
        })}
      >
        {isDownloadInProgress ? <NeueLoadingSpinner size='16' theme='default' /> : renderDownloadIcon()}
      </button>
      <MiniModal
        subtitle={<span className='text-neue-journey-medium-strong text-bedrock-white'>Download Journey visitors</span>}
        width='524px'
        openRef={modalOpenRef}
        closeRef={modalCloseRef}
      >
        <div className='flex flex-col gap-4'>
          <div className='flex flex-col gap-4 text-neue-journey-medium'>
            <div className='flex flex-col gap-2'>
              <span>The visitors for the listed journeys will be downloaded.</span>
              <ul className='list-disc pl-10 max-h-[30vh] overflow-y-auto'>
                {Object.values(journeys).map((journey) => (
                  <li key={journey.name}>{journey.name}</li>
                ))}
              </ul>
            </div>
            <span className='text-neue-journey-small'>
              Preparing the files might take some time. Please wait while the visitor list for each journey is
              downloaded individually.
            </span>
          </div>
          <Button
            size='regular'
            variant='secondary'
            label='Download'
            disabled={isDownloadInProgress}
            className='self-end'
            onClick={onMiniModalDownloadClick}
          />
        </div>
      </MiniModal>
      {visitorsData.map(({ name, data }) => (
        <DashboardInsightsJourneyDownloadVisitorsCSVLink
          data={data}
          fileName={`${name}_visitors_${new Date().toISOString()}.csv`}
          onComplete={() => {
            downloadedFileCountRef.current += 1;
            if (downloadedFileCountRef.current === visitorsData.length) {
              onMiniModalClose(true);
            }
          }}
        />
      ))}
    </>
  );
};
